import { api } from "@/lib/api";
import type { BannerCatalog, BannerPlatform } from "@/types/banner-catalog";

const BASE = "/admin/banners";

export interface BannerAssetDto {
    id: number;
    bannerId: number;
    formatSlug: string;
    kind: "image" | "video" | "text" | "logo";
    textSlot: string | null;
    variantIdx: number;
    url: string | null;
    content: string | null;
    mime: string | null;
    width: number | null;
    height: number | null;
    sizeBytes: number | null;
    sourceChatAttachmentId: number | null;
    validationState: "pending" | "ok" | "warn" | "error";
    validationMessage: string | null;
    createdAt: string;
}

export interface BannerValidationIssueDto {
    severity: "error" | "warn" | "info";
    code: string;
    formatSlug: string | null;
    textSlot: string | null;
    variantIdx: number | null;
    message: string;
}

export interface BannerValidationRunDto {
    id: number;
    bannerId: number;
    startedAt: string;
    finishedAt: string | null;
    passed: boolean | null;
    issues: BannerValidationIssueDto[];
    summary: string | null;
}

export interface BannerDto {
    id: number;
    pageId: number;
    name: string;
    platform: string;
    brief: string | null;
    status: "draft" | "generating" | "validating" | "ready" | "failed";
    createdByUserId: number | null;
    createdByEmail: string | null;
    createdAt: string;
    updatedAt: string;
    assetCount: number;
    assets: BannerAssetDto[];
    lastValidation: BannerValidationRunDto | null;
    /** Optional: backend exposes once banner advertiser FK is surfaced on the DTO. */
    advertiserId?: number | null;
}

export interface CreateBannerRequest {
    pageId: number;
    name: string;
    platform: string;
    brief?: string | null;
}

export async function getBannerCatalog(): Promise<BannerCatalog> {
    const { data } = await api.get<BannerCatalog>(`${BASE}/catalog`);
    return data;
}

export async function listBannerPlatforms(): Promise<BannerPlatform[]> {
    const { data } = await api.get<BannerPlatform[]>(`${BASE}/catalog/platforms`);
    return data;
}

export async function getBannerPlatform(id: string): Promise<BannerPlatform> {
    const { data } = await api.get<BannerPlatform>(`${BASE}/catalog/platforms/${id}`);
    return data;
}

export async function listBanners(pageId: number): Promise<BannerDto[]> {
    const { data } = await api.get<BannerDto[]>(BASE, { params: { pageId } });
    return data;
}

export async function getBanner(id: number): Promise<BannerDto> {
    const { data } = await api.get<BannerDto>(`${BASE}/${id}`);
    return data;
}

export async function createBanner(req: CreateBannerRequest): Promise<BannerDto> {
    const { data } = await api.post<BannerDto>(BASE, req);
    return data;
}

export async function deleteBanner(id: number): Promise<void> {
    await api.delete(`${BASE}/${id}`);
}

/**
 * One-click retry for a failed or stuck (generating/validating) banner.
 * Re-validates when there's rendered output, otherwise resets to draft.
 * Returns nothing useful to the caller — refetch the banner list after.
 */
export async function retryBanner(id: number): Promise<void> {
    await api.post(`${BASE}/${id}/retry`);
}

export async function deleteBannerAsset(bannerId: number, assetId: number): Promise<void> {
    await api.delete(`${BASE}/${bannerId}/assets/${assetId}`);
}

export function bannerDownloadUrl(id: number): string {
    return `${BASE}/${id}/download`;
}

export function bannerAssetUrl(bannerId: number, assetId: number): string {
    return `${BASE}/${bannerId}/assets/${assetId}/raw`;
}
